import type { CoverageEntry } from '../api/masterCertificateApi';

type YesNoNa = 'yes' | 'no' | 'na' | '';

// ── Form data shape exposed via forwardRef ────────────────────────────────────

export interface Acord28FormData {
  certDate: Date | null;
  producerAddr1: string;
  producerAddr2: string;
  producerAddr3: string;
  producerCity: string;
  producerState: string;
  producerZip: string;
  producerPhone: string;
  producerFax: string;
  producerEmail: string;
  companyName: string;
  naicNo: string;
  policyType: string;
  loanNumber: string;
  policyNumber: string;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  continuedUntilTerminated: boolean;
  replacesPriorDate: Date | null;
  insuredAddr1: string;
  insuredAddr2: string;
  insuredAddr3: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
  additionalNamedInsureds: string;
  propBuilding: boolean;
  propBusinessPersonalProp: boolean;
  locationDescription: string;
  perilsInsured: boolean;
  perilsBasic: boolean;
  perilsBroad: boolean;
  perilsSpecial: boolean;
  commPropCovCheck: boolean;
  commPropCovText: string;
  commPropAmount: string;
  commPropDed: string;
  businessIncome: boolean;
  rentalValue: boolean;
  birvYesNoNa: YesNoNa;
  birvLimit: string;
  birvActualLoss: boolean;
  birvMonths: string;
  blanketCoverage: YesNoNa;
  blanketAmount: string;
  terrorismCoverage: YesNoNa;
  terrorismSpecificExclusion: YesNoNa;
  domesticTerrorismExcluded: YesNoNa;
  limitedFungus: YesNoNa;
  limitedFungusLimit: string;
  limitedFungusDed: string;
  fungusExclusion: YesNoNa;
  fungusExclusionForm: string;
  fungusExclusionDate: Date | null;
  fungusExclusionText: string;
  replacementCost: YesNoNa;
  agreedValue: YesNoNa;
  coinsurance: YesNoNa;
  coinsurancePercent: string;
  equipmentBreakdown: YesNoNa;
  equipmentBreakdownLimit: string;
  equipmentBreakdownDed: string;
  ordinanceOrLaw: YesNoNa;
  ordinanceLimit: string;
  ordinanceDed: string;
  demolitionCostsYN: YesNoNa;
  demolitionLimit: string;
  demolitionDed: string;
  incrCostYN: YesNoNa;
  incrCostLimit: string;
  incrCostDed: string;
  earthMovement: YesNoNa;
  earthMovementLimit: string;
  earthMovementDed: string;
  flood: YesNoNa;
  floodLimit: string;
  floodDed: string;
  windHailInclYes: boolean;
  windHailInclNo: boolean;
  windHail: YesNoNa;
  windHailLimit: string;
  windHailDed: string;
  namedStormInclYes: boolean;
  namedStormInclNo: boolean;
  namedStorm: YesNoNa;
  namedStormLimit: string;
  namedStormDed: string;
  waiverSubrogation: YesNoNa;
  additionalInterestName: string;
  lenderServicingAgent: string;
  authRepSignature: string;
  remarks: string;
  companyNaicId: string | null;
  // ACORD 101
  showAcord101: boolean;
  acord101Agency: string;
  acord101NamedInsured: string;
  acord101AdditionalRemarks: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function toDisplayDate(d: Date | null): string {
  if (!d) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${d.getFullYear()} 12:00:00 AM`.replace(`${m}/`, `${m}/${day}/`);
}

function toIsoDateTime(d: Date | null): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}T00:00:00`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function yn(val: YesNoNa): string {
  if (val === 'yes') return 'Yes';
  if (val === 'no')  return 'No';
  if (val === 'na')  return 'na';
  return '';
}

function ch(val: boolean): string { return val ? 'Yes' : 'No'; }

// ── XML builder ───────────────────────────────────────────────────────────────

function buildAcord28Xml(d: Acord28FormData): string {
  const fungusDate = d.fungusExclusionDate
    ? `${String(d.fungusExclusionDate.getMonth() + 1).padStart(2, '0')}/${String(d.fungusExclusionDate.getDate()).padStart(2, '0')}/${d.fungusExclusionDate.getFullYear()}`
    : '';

  return (
    '<Coverages>' +
    '<Acord28AllLiabilities>' +
    `<CompletionDate>${esc(toDisplayDate(d.certDate))}</CompletionDate>` +
    `<ProducerName>${esc(d.producerAddr1)}</ProducerName>` +
    `<ProducerAddressLineOne>${esc(d.producerAddr2)}</ProducerAddressLineOne>` +
    `<ProducerAddressLineTwo>${esc(d.producerAddr3)}</ProducerAddressLineTwo>` +
    `<ProducerCity>${esc(d.producerCity)}</ProducerCity>` +
    `<ProducerState>${esc(d.producerState)}</ProducerState>` +
    `<ProducerZIPCode>${esc(d.producerZip)}</ProducerZIPCode>` +
    `<InsuredName>${esc(d.insuredAddr1)}</InsuredName>` +
    `<AddressLineOne>${esc(d.insuredAddr2)}</AddressLineOne>` +
    `<AddressLineTwo>${esc(d.insuredAddr3)}</AddressLineTwo>` +
    `<City>${esc(d.insuredCity)}</City>` +
    `<State>${esc(d.insuredState)}</State>` +
    `<ZIPCode>${esc(d.insuredZip)}</ZIPCode>` +
    `<AdditionalInsuredName>${esc(d.additionalNamedInsureds)}</AdditionalInsuredName>` +
    `<Email>${esc(d.producerEmail)}</Email>` +
    `<Phone>${esc(d.producerPhone)}</Phone>` +
    `<Fax>${esc(d.producerFax)}</Fax>` +
    `<LoanNumber>${esc(d.loanNumber)}</LoanNumber>` +
    `<NaicNo>${esc(d.naicNo)}</NaicNo>` +
    `<ContinuedUntilTerminated>${d.continuedUntilTerminated ? 'Yes' : 'No'}</ContinuedUntilTerminated>` +
    `<PriorEvidanceDate>${d.replacesPriorDate ? `${String(d.replacesPriorDate.getMonth()+1).padStart(2,'0')}/${String(d.replacesPriorDate.getDate()).padStart(2,'0')}/${d.replacesPriorDate.getFullYear()}` : ''}</PriorEvidanceDate>` +
    `<LocationDescription>${esc(d.locationDescription)}</LocationDescription>` +
    `<Other text="" value="" />` +
    `<TerrorismExclusion text="">${yn(d.terrorismSpecificExclusion)}</TerrorismExclusion>` +
    `<ActualLossSustained text="${esc(d.birvMonths)}">${ch(d.birvActualLoss)}</ActualLossSustained>` +
    `<AgreedValue text="">${yn(d.agreedValue)}</AgreedValue>` +
    `<PerilsInsured>${ch(d.perilsInsured)}</PerilsInsured>` +
    `<Basic>${ch(d.perilsBasic)}</Basic>` +
    `<Blanket text="${esc(d.blanketAmount)}">${yn(d.blanketCoverage)}</Blanket>` +
    `<Broad>${ch(d.perilsBroad)}</Broad>` +
    `<Building>${ch(d.propBuilding)}</Building>` +
    `<BusinessIncome>${ch(d.businessIncome)}</BusinessIncome>` +
    `<BusinessLimits text="${esc(d.birvLimit)}">${yn(d.birvYesNoNa)}</BusinessLimits>` +
    `<BusinessPersonalProperty>${ch(d.propBusinessPersonalProp)}</BusinessPersonalProperty>` +
    `<Coinsurance text="${esc(d.coinsurancePercent)}">${yn(d.coinsurance)}</Coinsurance>` +
    `<Construction text="${esc(d.incrCostLimit)}" value="${esc(d.incrCostDed)}">${yn(d.incrCostYN)}</Construction>` +
    `<Demolition text="${esc(d.demolitionLimit)}" value="${esc(d.demolitionDed)}">${yn(d.demolitionCostsYN)}</Demolition>` +
    `<EarthMovement text="${esc(d.earthMovementLimit)}" value="${esc(d.earthMovementDed)}">${yn(d.earthMovement)}</EarthMovement>` +
    `<EquipmentBreakown text="${esc(d.equipmentBreakdownLimit)}" value="${esc(d.equipmentBreakdownDed)}">${yn(d.equipmentBreakdown)}</EquipmentBreakown>` +
    `<Flood text="${esc(d.floodLimit)}" value="${esc(d.floodDed)}">${yn(d.flood)}</Flood>` +
    `<FungusExclusion text="">${yn(d.fungusExclusion)}</FungusExclusion>` +
    `<FungusExclusion_InsurerIdentifier>${esc(d.fungusExclusionForm)}</FungusExclusion_InsurerIdentifier>` +
    `<FungusExclusion_EditionDate>${esc(fungusDate)}</FungusExclusion_EditionDate>` +
    `<FungusExclusion_EntityCopyrightOwnership>${esc(d.fungusExclusionText)}</FungusExclusion_EntityCopyrightOwnership>` +
    `<LimitedFungus text="${esc(d.limitedFungusLimit)}" value="${esc(d.limitedFungusDed)}">${yn(d.limitedFungus)}</LimitedFungus>` +
    `<Loss text="${esc(d.ordinanceLimit)}" value="${esc(d.ordinanceDed)}">${yn(d.ordinanceOrLaw)}</Loss>` +
    `<OtherCoverage text="${esc(d.commPropCovText)}">${ch(d.commPropCovCheck)}</OtherCoverage>` +
    `<Permission text="">${yn(d.waiverSubrogation)}</Permission>` +
    `<PolicyType>${esc(d.policyType)}</PolicyType>` +
    `<RentalValue>${ch(d.rentalValue)}</RentalValue>` +
    `<ReplacementCost text="">${yn(d.replacementCost)}</ReplacementCost>` +
    `<Special>${ch(d.perilsSpecial)}</Special>` +
    `<TerrorismExcluded text="">${yn(d.domesticTerrorismExcluded)}</TerrorismExcluded>` +
    `<TerrorismExclusion text="">${yn(d.terrorismSpecificExclusion)}</TerrorismExclusion>` +
    `<Terrorism>${yn(d.terrorismCoverage)}</Terrorism>` +
    `<Wind text="${esc(d.windHailLimit)}" value="${esc(d.windHailDed)}">${yn(d.windHail)}</Wind>` +
    `<Wind_Yes>${ch(d.windHailInclYes)}</Wind_Yes>` +
    `<Wind_No>${ch(d.windHailInclNo)}</Wind_No>` +
    `<NamedStorm text="${esc(d.namedStormLimit)}" value="${esc(d.namedStormDed)}" yes="${ch(d.namedStormInclYes)}" no="${ch(d.namedStormInclNo)}">${yn(d.namedStorm)}</NamedStorm>` +
    `<EvidenceCommercialProperty>${esc(d.remarks)}</EvidenceCommercialProperty>` +
    `<CoverageDeductible>${esc(d.commPropDed)}</CoverageDeductible>` +
    `<CoverageAmount>${esc(d.commPropAmount)}</CoverageAmount>` +
    `<AdditionalInterestNameAddress>${esc(d.additionalInterestName)}</AdditionalInterestNameAddress>` +
    `<LenderServicingAgentNameAddress>${esc(d.lenderServicingAgent)}</LenderServicingAgentNameAddress>` +
    '</Acord28AllLiabilities>' +
    '</Coverages>'
  );
}

function buildAcord101Xml(d: Acord28FormData): string {
  return (
    `<Coverages><Acord101AdditionalRemarks>` +
    `<RemarksText>${esc(d.acord101AdditionalRemarks)}</RemarksText>` +
    `</Acord101AdditionalRemarks></Coverages><Limits />`
  );
}

function buildAcord28AdditionalInterestXml(d: Acord28FormData): string {
  return (
    '<Coverages><CertificateHolderInformationCoverage>' +
    '<AdditionalInterest>' +
    `<NameAndAddress>${esc(d.additionalInterestName)}</NameAndAddress>` +
    `<AdditionalInsured>false</AdditionalInsured>` +
    `<LendersLossPayable>false</LendersLossPayable>` +
    `<LossPayee>false</LossPayee>` +
    `<Mortage>false</Mortage>` +
    `<Other>false</Other>` +
    `<OtherDescription></OtherDescription>` +
    `<LoanNumber></LoanNumber>` +
    `<LenderServicingAgentNameAndAddress>${esc(d.lenderServicingAgent)}</LenderServicingAgentNameAndAddress>` +
    '</AdditionalInterest>' +
    '</CertificateHolderInformationCoverage></Coverages>'
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function buildAcord28Coverages(d: Acord28FormData, acordFormNumber = 2816): CoverageEntry[] {
  const sig = d.authRepSignature || null;
  const entries: CoverageEntry[] = [
    {
      PolicyNumber:       d.policyNumber,
      NaicId:             d.companyNaicId ?? null,
      EffectiveDate:      toIsoDateTime(d.effectiveDate),
      ExpirationDate:     toIsoDateTime(d.expirationDate),
      AcordFormNumber:    acordFormNumber,
      CoveragesAndLimits: buildAcord28Xml(d),
      SignatureId:        sig,
    },
  ];
  if (d.showAcord101) {
    entries.push({
      PolicyNumber:       '',
      NaicId:             null,
      EffectiveDate:      '',
      ExpirationDate:     '',
      AcordFormNumber:    101,
      CoveragesAndLimits: buildAcord101Xml(d),
      SignatureId:        sig,
    });
  }
  if (d.additionalInterestName) {
    entries.push({
      PolicyNumber:       d.policyNumber,
      NaicId:             d.companyNaicId ?? null,
      EffectiveDate:      toIsoDateTime(d.effectiveDate),
      ExpirationDate:     toIsoDateTime(d.expirationDate),
      AcordFormNumber:    2716,
      CoveragesAndLimits: buildAcord28AdditionalInterestXml(d),
      SignatureId:        sig,
    });
  }
  return entries;
}
